// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCsvExport } from '@hooks/other/useCsvExport';
import { useDialogControl } from '@hooks/other/useDialogControl';
import {
  useGetAllInstrumentConfigurations,
  useInstrumentConfigurationsById,
} from '@hooks/queries/useGetAllInstrumentConfigurations';
import { useSnackbar } from 'notistack';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import CopyableText from '../../../components/CopyableText';
import { DataTable } from '../../../components/Table/DataTable';
import { useRequestUnfreeze } from '../../../hooks/mutations/registry/freeze/useRequestUnfreeze';
import useCredentials from '../../../hooks/queries/credential/useCredentials';
import { useOperatorDenyLists } from '../../../hooks/queries/registry/denyList/useDenyLists';
import useRegistrarServices from '../../../hooks/queries/registry/onboarding/useRegistrarServices';
import useHoldings from '../../../hooks/queries/registry/useHoldings';
import { formatTimestamp, parseTimestampToDate } from '../../../utils/common';
import { filterInstConfigsByInstrument } from '../../../utils/filterInstConfigsByInstrument';
import { PageWrapper } from '../../PageWrapper';
import { RequestFreezeDialog } from './RequestFreezeDialog';
import { Denial } from './types';

interface FreezesProps {
  disableVirtualization?: boolean;
}

export const Freezes: React.FC<FreezesProps> = ({ disableVirtualization }) => {
  const { setDialogName, dialogName, onCloseDialog } = useDialogControl();
  const navigate = useNavigate();

  const registrarServices = useRegistrarServices();
  const requestUnfreeze = useRequestUnfreeze();
  const credentials = useCredentials();
  const denyLists = useOperatorDenyLists();
  const holdings = useHoldings();
  const instConfigs = useGetAllInstrumentConfigurations();
  const { enqueueSnackbar } = useSnackbar();

  const denyListsByName = useMemo(
    () => Object.fromEntries((denyLists.data ?? []).map((d) => [d.payload.name, d])),
    [denyLists.data],
  );

  const instrumentsById = useInstrumentConfigurationsById();

  const denials = useMemo(
    () =>
      (credentials.data ?? []).flatMap((c) =>
        c.payload.claims
          .filter(
            (claim) =>
              claim.subject === c.payload.holder && claim.property === 'isMemberOfDenyList',
          )
          .map((claim) => claim.value)
          .flatMap((dlName) => {
            const denyList = denyListsByName[dlName];
            const denial = denyList?.payload.denials;
            return denyList && denial && denial.tag === 'Freeze'
              ? [
                  {
                    holder: c.payload.holder,
                    instrumentId: denial.value.id,
                    instrumentRegistrar: denial.value.admin,
                    holderCredentialContract: c,
                    denyList,
                  },
                ]
              : [];
          }),
      ),
    [credentials.data, denyListsByName],
  );

  const unfreeze = useCallback(
    async (denial: Denial) => {
      const registrarService = registrarServices.data && registrarServices.data[0];
      if (!registrarService) {
        enqueueSnackbar('No registrar service found.', { variant: 'error' });
        return;
      }

      const instConfig = filterInstConfigsByInstrument(
        denial.instrumentId,
        denial.instrumentRegistrar,
        instConfigs.data,
      );

      if (!instConfig) {
        enqueueSnackbar(
          `Cannot find instrument configuration for instrument ID: ${denial.instrumentId}.`,
          { variant: 'error' },
        );
        return;
      }

      await requestUnfreeze({
        registrarServiceCid: registrarService.contractId,
        credentialCids: [denial.holderCredentialContract.contractId],
        denyListCids: [denial.denyList.contractId],
        holdingCids: (holdings.data ?? [])
          .filter(
            (h) =>
              h.payload.owner === denial.holder && h.payload.instrument.id === denial.instrumentId,
          )
          .map((h) => h.contractId),
        instrumentConfigurationCid: instConfig.contract.contractId,
      });
    },
    [registrarServices.data, instConfigs.data, holdings.data, requestUnfreeze, enqueueSnackbar],
  );

  const headers: GridColDef<Denial>[] = useMemo(
    () => [
      {
        field: 'holder',
        headerName: 'Holder',
        valueGetter: (params) => params.row.holder,
        flex: 2,
        filterable: true,
      },
      {
        field: 'instrumentId',
        headerName: 'Instrument Id',
        valueGetter: (params) => params.row.instrumentId,
        renderCell: (params) => <CopyableText value={params.row.instrumentId} />,
        flex: 2,
        filterable: true,
      },
      {
        field: 'tokenName',
        headerName: 'Token Name',
        valueGetter: (params) => instrumentsById[params.row.instrumentId]?.payload.tokenName || '',
        flex: 2,
        filterable: true,
      },
      {
        field: 'tokenSymbol',
        headerName: 'Token Symbol',
        valueGetter: (params) =>
          instrumentsById[params.row.instrumentId]?.payload.tokenSymbol || '',
        flex: 2,
        filterable: true,
      },
      {
        field: 'cusip',
        headerName: 'CUSIP',
        valueGetter: (params) => instrumentsById[params.row.instrumentId]?.payload.cusip || '',
        flex: 2,
        filterable: true,
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        type: 'dateTime',
        valueGetter: (params) => parseTimestampToDate(params.row.denyList.createdAt),
        renderCell: (params) => formatTimestamp(params.row.denyList.createdAt),
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        renderCell: (params) => (
          <AsyncButton sx={{ mr: 1 }} onClick={() => unfreeze(params.row)}>
            Unfreeze
          </AsyncButton>
        ),
        flex: 0,
        filterable: true,
      },
    ],
    [unfreeze, instrumentsById],
  );

  const { handleExport, isExportDisabled } = useCsvExport(headers, denials, {
    filename: 'freezes',
  });

  const buttons = [
    {
      name: 'Export CSV',
      onClick: handleExport,
      disabled: isExportDisabled,
    },
    {
      name: 'Request Freeze',
      onClick: () => setDialogName('freeze'),
    },
    {
      name: 'See Freeze History',
      onClick: () => navigate('/registry/freeze-history'),
    },
  ];

  return (
    <PageWrapper
      buttons={buttons}
      pageName={
        <Box sx={{ alignItems: 'center', display: 'flex', flex: 1 }}>
          <Typography
            sx={{ textTransform: 'capitalize' }}
            fontWeight="bold"
            color="primary"
            variant="h4"
          >
            Freezes
          </Typography>
        </Box>
      }
    >
      {dialogName === 'freeze' && (
        <RequestFreezeDialog
          open
          onClose={onCloseDialog}
          denyLists={denyLists.data ?? []}
          denials={denials}
        />
      )}
      <DataTable
        columns={headers}
        rows={denials}
        rowKey={(d) => `${d.holder}-${d.instrumentId}`}
        disableVirtualization={disableVirtualization}
      />
    </PageWrapper>
  );
};
